import 'maplibre-gl/dist/maplibre-gl.css'

import { useCallback, useEffect, useState } from 'react'
import { Map, MapRef, Marker, type MarkerDragEvent, type ViewStateChangeEvent } from '@vis.gl/react-maplibre'

import { getBoundsForCoordinates } from '@consumer/shared/geo'

import type { OrderMapProps } from './types'

const MAP_MIN_DELTA = 0.0025

/** Approximate zoom level from a lat/lng delta (inverse of the mercator formula). */
function deltaToZoom(delta: number): number {
  return Math.max(3, Math.min(20, Math.log2(360 / delta)))
}

interface ViewState {
  latitude: number
  longitude: number
  zoom: number
  bearing: number
  pitch: number
}

const US_CENTER: ViewState = {
  latitude: 39.9532,
  longitude: -98.6024,
  zoom: 3,
  bearing: 0,
  pitch: 0,
}

function initialViewFromPins(
  pins: OrderMapProps['pins'],
  viewRatio: number,
  bottomPaddingRatio: number | undefined
): ViewState {
  if (pins.length === 0) return US_CENTER

  const bounds = getBoundsForCoordinates(pins.map(p => p.coordinate))

  const latDelta = Math.max((bounds.maxLat - bounds.minLat) * 2, MAP_MIN_DELTA)
  const lngDelta = Math.max((bounds.maxLng - bounds.minLng) * 2, MAP_MIN_DELTA)
  const verticalDelta = latDelta * viewRatio > lngDelta ? latDelta : (lngDelta / viewRatio) * 0.6
  const latitude =
    bottomPaddingRatio !== undefined && verticalDelta > 0
      ? bounds.centerLat - verticalDelta * bottomPaddingRatio
      : bounds.centerLat

  const zoom = pins.length === 1 ? 16 : deltaToZoom(Math.max(latDelta, lngDelta))

  return { latitude, longitude: bounds.centerLng, zoom, bearing: 0, pitch: 0 }
}

/** Web-only extensions to OrderMapProps — not part of the cross-platform API. */
interface OrderMapWebProps extends OrderMapProps {
  /** Forwarded to the underlying MapRef so callers can call easeTo/panTo. */
  mapRef?: React.RefObject<MapRef>
  /** Called whenever the map view state changes (pan, zoom, etc.). */
  onMove?: (evt: ViewStateChangeEvent) => void
}

/**
 * Web implementation of OrderMap.
 *
 * Wraps `@vis.gl/react-maplibre` and fits the initial camera to show all
 * `pins` using `getBoundsForCoordinates` from `@consumer/shared/geo`.
 *
 * Pass `mapStyleUrl` from the app's environment variables (AWS Location
 * Service style endpoint). Without it the map renders without a tile layer.
 *
 * Pass `children` (e.g. app-level MapMarker / Popup components) to override
 * the default pin rendering. `pins` is still used for camera fitting.
 *
 * Platform-specific overlays (address strip, geopin correction UI, etc.) are
 * the responsibility of the calling page — this component is a pure renderer.
 */
export const OrderMap = ({
  pins,
  fullScreenMap = false,
  viewRatio = 1,
  bottomPaddingRatio,
  mapStyleUrl,
  onPress,
  children,
  mapRef,
  onMove: onMoveProp,
}: OrderMapWebProps) => {
  const [mapView, setMapView] = useState<ViewState>(() => initialViewFromPins(pins, viewRatio, bottomPaddingRatio))

  // Re-fit the camera whenever pin coordinates change (e.g. after authentication
  // populates the exact address location). Skip in fullScreenMap so we don't
  // override the user's interactive pan/zoom.
  useEffect(() => {
    if (!fullScreenMap) {
      setMapView(initialViewFromPins(pins, viewRatio, bottomPaddingRatio))
    }
  }, [pins, viewRatio, bottomPaddingRatio, fullScreenMap])

  const handleMove = useCallback(
    (evt: ViewStateChangeEvent) => {
      const { latitude, longitude, zoom, bearing, pitch } = evt.viewState
      setMapView({ latitude, longitude, zoom, bearing, pitch })
      onMoveProp?.(evt)
    },
    [onMoveProp]
  )

  return (
    <Map
      {...mapView}
      ref={mapRef}
      style={{ width: '100%', height: '100%' }}
      mapStyle={mapStyleUrl}
      onMove={handleMove}
      onClick={onPress}
      dragPan={fullScreenMap}
      scrollZoom={fullScreenMap}
      doubleClickZoom={fullScreenMap}
      dragRotate={false}
      pitchWithRotate={false}
    >
      {children ??
        pins.map(pin => (
          <Marker
            key={pin.key}
            longitude={pin.coordinate.lng}
            latitude={pin.coordinate.lat}
            onClick={
              pin.onPress
                ? e => {
                    // Prevent the click from bubbling to the Map's onClick
                    // handler (onPress), which is documented as background-only.
                    e.originalEvent?.stopPropagation()
                    pin.onPress!()
                  }
                : undefined
            }
            draggable={pin.draggable}
            onDragEnd={
              pin.draggable && pin.onDragEnd
                ? (e: MarkerDragEvent) => pin.onDragEnd!({ lat: e.lngLat.lat, lng: e.lngLat.lng })
                : undefined
            }
          />
        ))}
    </Map>
  )
}
